import { useMutation } from '@tanstack/react-query'
import { postData } from '../../apis/api.helpers'
import apiUrl from '../../apis/api.url'
import { useAppStore } from '../../store/appStore'
import { useAuthStore } from '../../store/authStore'
import { LoginSchema } from './schema'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useSnackbarManager } from '../../components/common/snackbar'
import { getErrorMessage } from '../../utilities/parsers'

export const ROLE_MAP: Record<number, string> = {
  2: 'aspering_ent',
  3: 'schools',
  4: 'existing_ent',
  5: 'research',
  6: 'industry_asso',
  7: 'mentors',
  8: 'edclub',
  9: 'students',
  10: 'guest',
}

export const ROLE_DISPLAY_NAME: Record<string, string> = {
  2: 'Aspiring Entrepreneur',
  3: 'School',
  4: 'Existing Entrepreneur',
  5: 'Research Scholar',
  6: 'Industry Association',
  7: 'Mentor',
  8: 'Entrepreneurship Club',
  9: 'Student',
  10: 'Guest',
}

export const useLogin = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { setToken, setAuthenticated, setRoleData, setUserData } =
    useAuthStore()
  const { setIsLoading } = useAppStore()

  const loginMutation = useMutation(
    async (params: LoginSchema) => {
      setIsLoading(true)
      const response = await postData(apiUrl.LOGIN, params)
      return response
    },
    {
      onSuccess: (data: any) => {
        const { token, user } = data
        setToken(token)
        setAuthenticated(true)
        setUserData({
          id: user.id,
          name: user.name,
          email: user.email,
          mobile: user.mobile,
          role: user.role,
          is_profile_complete: user.is_profile_complete,
        })
        const roleName = ROLE_MAP[user.role] || `Role ${user.role}`
        setRoleData({
          id: user.role,
          name: roleName,
        })

        enqueueSnackbar('Login successful', { variant: 'success' })
        handleSubmission(data)
        navigate('/dashboard')
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Login failed. Please try again.'
        enqueueSnackbar(message, { variant: 'error' })
        console.error('Login error:', error)
      },
      onSettled: () => {
        setIsLoading(false)
      },
    }
  )

  return loginMutation
}

export const forgotPassword = (input: { email: string }) => {
  return postData(`${apiUrl.FORGOT_PASSWORD}`, input)
}

export const useForgotPassword = (onSuccessHandler?: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation({
    mutationFn: async (input: { email: string }) => {
      return forgotPassword(input)
    },
    onSuccess: (res: any) => {
      if (onSuccessHandler) onSuccessHandler(res.data)
      enqueueSnackbar(
        res?.data?.message || 'Password reset link sent to your email.',
        { variant: 'success' }
      )
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        { variant: 'error' }
      )
    },
  })
}
